// @erp/ui/progress-bar — the thin accent bar that says "the server is working".
//
// INDETERMINATE BY DEFAULT: a request's duration is unknown, and a fake
// percentage is a lie. daisyUI does the whole job natively — a `<progress>`
// with NO `value` attribute matches `:indeterminate`, and daisyUI animates that
// state itself (`.progress:indeterminate`, a repeating gradient swept by its
// `progress` keyframes). Nothing here re-implements it; this component only
// picks the accent, the thickness and the square ends, and owns the decision of
// when a bar is the right feedback at all.
//
// DETERMINATE WHEN, AND ONLY WHEN, THE WORK IS COUNTABLE (`value`, added
// 2026-09-30 for the phone capture pipeline). Passing a number switches the
// same element to a filled bar. Pass one only where something real is being
// counted — rungs of a compression ladder, bytes of an upload — never a timer
// animated to look like progress. The indeterminate sweep is the honest answer
// to "we do not know", and it is the default for that reason.
//
// WHERE IT BELONGS: on the EDGE of the thing being updated — under a table's
// header, under a dropdown's search field. That placement is the message: it
// says which region is about to change, which a floating spinner never does.
// It is not a substitute for a skeleton (first load, when there is no content
// yet) or for a button's own pending state (an action the user just took).
import { Show, createEffect } from 'solid-js';

export interface ProgressBarProps {
  /** Show it. Off = nothing renders, so it costs no layout when idle. */
  active?: boolean;
  /** What is loading, for screen readers. */
  label?: string;
  /**
   * 0…1 of REAL, counted progress — the bar fills instead of sweeping. Omit it
   * (the default) whenever the duration is unknown. Values outside the range
   * are clamped rather than left to the browser, which renders `value="1.4"`
   * as an empty bar on some engines.
   */
  value?: number;
  class?: string;
}

export function ProgressBar(props: ProgressBarProps) {
  /* DETERMINACY IS SET ON THE ELEMENT, not through a JSX attribute, and the
     reason is worth the four lines. Solid compiles `value`/`max` on a
     <progress> to PROPERTY assignments: `el.value = undefined` throws, which
     took out fifteen DataTable tests the first time this was written the
     obvious way. And :indeterminate matches the ABSENCE of the attribute — a
     `value="0"` is a determinate bar sitting at zero, which reads as stuck.
     So the attribute is added and REMOVED here, where both cases are explicit
     and neither depends on how a framework treats undefined. */
  const setDeterminacy = (el: HTMLProgressElement): void => {
    createEffect(() => {
      if (props.value === undefined) {
        el.removeAttribute('value');
        el.removeAttribute('max');
        return;
      }
      el.setAttribute('value', String(Math.max(0, Math.min(1, props.value))));
      el.setAttribute('max', '1');
    });
  };

  return (
    <Show when={props.active}>
      {/* h-1 = 4px = --spectrum-progress-bar-thickness-small, Spectrum's
          thinnest step (desktop scale; the touch scale's is 5px).
          rounded-none: daisyUI gives a progress the 16px radius-box, which on
          a 4px full-bleed strip rounds the ends into lozenges — a bar spanning
          an edge is square, like the edge it sits on.

          block + align-top: <progress> is inline-block by default, so it sits
          on the text baseline and leaves a descender gap underneath that reads
          as a stray margin under the bar. */}
      <progress
        class={`progress progress-primary block h-1 w-full rounded-none align-top ${props.class ?? ''}`}
        aria-label={props.label ?? 'Loading'}
        ref={setDeterminacy}
      />
    </Show>
  );
}
